import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/stocktakes/components/StocktakesToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { CompanyData, getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { RootState } from '@/setup/redux/RootReducer';

import { createDefaultStocktakeForm, stocktakeStatusOptions } from '../stocktakesMockData';
import { getStocktakes } from '../redux';
import { InventoryStocktakeFormData, InventoryStocktakeRow } from '../types';
import StocktakeForm from './StocktakeForm';
import StocktakeList, { StocktakeListHandle } from './StocktakeList';

type StocktakeCompanyOption = {
  company_id: string;
  company_name: string;
};

const buildFormMetadata = (formData: InventoryStocktakeFormData, stocktakeId?: string) => ({ formData, stocktakeId });
const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const formStatusOptions = stocktakeStatusOptions.filter((option) => option.id !== 'all');

const resolveCompanyIdForQuery = (company: string) => {
  if (!company || company === 'all' || company === 'global') {
    return '';
  }
  return company;
};

export default function StocktakesModule() {
  const companies = useSelector((state: RootState) => (state.auth.companies || []) as CompanyData[]);
  const hasGlobalRole = useSelector((state: RootState) => state.auth?.hasGlobalRole ?? false);
  const listRef = useRef<StocktakeListHandle>(null);

  const companyOptions = useMemo<StocktakeCompanyOption[]>(
    () =>
      companies.map((company: CompanyData) => ({
        company_id: company.company_id,
        company_name: company.company_name,
      })),
    [companies]
  );

  const [rows, setRows] = useState<InventoryStocktakeRow[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ company: 'all', status: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCompanyId = useMemo(
    () => String(getCurrentCompanyId() || '').trim(),
    [],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const { canCreate: canCreateStocktakes, resolveTabPermissions, resolvePermissionsForCompanyId, canReadRecord } = useModuleToolbarPermissions({
    resource: 'inventory.stocktake',
    activeCompanyFilter,
    updateTabTypes: ['update-stocktake'],
  });

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh sách kiểm kê', type: 'list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateStocktakes) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateStocktakes]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_stocktakes_tabs_v2',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateStocktakes),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-stocktake', 'update-stocktake'],
    allowMultipleTabTypes: ['new-stocktake', 'update-stocktake'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = resolveCompanyIdForQuery(activeCompanyFilter) || selectedCompanyId;
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `KK #${currentTabs.filter((tab) => tab.type === 'new-stocktake').length + 1}`,
        type: 'new-stocktake',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(createDefaultStocktakeForm(baseCompanyId)),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryStocktakeFormData | undefined;
  const activeStocktakeId = activeTabData?.metadata?.stocktakeId as string | undefined;
  const listScopeCompanyId = resolveCompanyIdForQuery(activeCompanyFilter);

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const loadStocktakes = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await getStocktakes({
        companyId: listScopeCompanyId,
        search: searchQuery || undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        page: 1,
        pageSize: PAGE_SIZE,
      });
      setRows(response.results);
    } catch (_error) {
      setSnackbarMessage({ message: 'Không thể tải danh sách kiểm kê.', severity: 'error' });
    } finally {
      setListLoading(false);
    }
  }, [filters.status, listScopeCompanyId, searchQuery]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void loadStocktakes();
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [loadStocktakes]);

  const updateFormData = (updates: Partial<InventoryStocktakeFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activeStocktakeId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeStocktakeId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
  };

  const openExistingStocktake = (row: InventoryStocktakeRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage({ message: 'Bạn không có quyền xem chi tiết phiếu kiểm kê.', severity: 'error' });
      return;
    }
    guardAndRun(() => {
      openTab({
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: row.code,
        type: 'update-stocktake',
        closable: true,
        entityId: row.id,
        metadata: {
          companyId: row.company_id,
          ...buildFormMetadata(
          {
            code: row.code,
            name: row.name,
            store_name: row.store_name,
            date: row.date,
            status: row.status,
            company_id: row.company_id,
            note: row.note || '',
          },
          row.id
        ),
        },
      });
    });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };

  const handleRefresh = () => {
    void loadStocktakes()
      .then(() => setSnackbarMessage({ message: 'Đã refresh danh sách kiểm kê.', severity: 'success' }))
      .catch(() => setSnackbarMessage({ message: 'Không thể refresh danh sách kiểm kê.', severity: 'error' }));
  };

  const handleSave = () => {
    if (!activeFormData || !activeTabData?.id) return;

    const isUpdateMode = Boolean(activeStocktakeId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật phiếu kiểm kê.', severity: 'error' });
      return;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Bạn không có quyền tạo phiếu kiểm kê.', severity: 'error' });
      return;
    }

    const company = companyOptions.find((entry) => entry.company_id === activeFormData.company_id);
    const nextRow: InventoryStocktakeRow = {
      id: activeStocktakeId || `st-${Date.now()}`,
      code: activeFormData.code,
      name: activeFormData.name,
      store_name: activeFormData.store_name,
      date: activeFormData.date,
      status: activeFormData.status,
      company_id: activeFormData.company_id,
      company_name: company?.company_name || 'Chưa rõ công ty',
      note: activeFormData.note,
    };

    setRows((current) =>
      current.some((row) => row.id === nextRow.id)
        ? current.map((row) => (row.id === nextRow.id ? nextRow : row))
        : [nextRow, ...current]
    );

    handleUnsavedChangesUpdate(false, activeTabData.id);
    setSnackbarMessage({ message: `Đã lưu ${nextRow.code || nextRow.name}.`, severity: 'success' });
    updateTab(activeTabData.id, {
      title: nextRow.code || nextRow.name,
      entityId: nextRow.id,
      metadata: buildFormMetadata(activeFormData, nextRow.id),
    });
  };

  const formCompanyOptions = useMemo(() => {
    if (!activeFormData?.company_id) {
      return companyOptions;
    }
    const exists = companyOptions.some((company) => company.company_id === activeFormData.company_id);
    if (exists) {
      return companyOptions;
    }
    return [
      {
        company_id: activeFormData.company_id,
        company_name: activeFormData.company_id,
      },
      ...companyOptions,
    ];
  }, [activeFormData?.company_id, companyOptions]);

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryStocktakeFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);

              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              status: filters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={stocktakeStatusOptions}
                            onRefresh={handleRefresh}
                          />
                        }
                        dataTable={
                          <StocktakeList
                            ref={listRef}
                            rows={rows}
                            loading={listLoading}
                            onSelectRow={openExistingStocktake}
                            onRefresh={() => void loadStocktakes()}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <StocktakeForm
                        formData={tabFormData}
                        companyDisabled={!hasGlobalRole}
                        companyOptions={formCompanyOptions}
                        statusOptions={formStatusOptions}
                        onChangeForm={(updates) => {
                          const canEditTab = tab.type === 'new-stocktake' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                          if (!canEditTab) {
                            return;
                          }
                          if (tab.id !== activeTabData?.id) {
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                { ...tabFormData, ...updates },
                                tab.metadata?.stocktakeId as string | undefined
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onSave={handleSave}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert
          severity={snackbarMessage?.severity || 'success'}
          onClose={() => setSnackbarMessage(null)}
          sx={{ width: '100%' }}
        >
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
